import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http } from "./api";
import { getSessionId, getUTM } from "./utils";

const CartCtx = createContext(null);
const KEY = "lo_cart_v1";

const emptyCart = { lines: [], discount_code: "", add_gift_bag: false, add_name_tag: false };

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || emptyCart; } catch { return emptyCart; }
  });
  const [calc, setCalc] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const recalc = useCallback(async (c) => {
    const src = c || cart;
    if (!src.lines.length) { setCalc(null); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/cart/calculate", src);
      setCalc(data);
    } finally { setBusy(false); }
  }, [cart]);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(cart));
    recalc(cart);
  }, [cart, recalc]);

  const track = useCallback((name, props = {}) => {
    http.post("/events", {
      name, session_id: getSessionId(), props, utm: getUTM(),
      referrer: document.referrer || null, device: window.innerWidth < 640 ? "mobile" : "desktop",
      url: window.location.pathname,
    }).catch(() => {});
  }, []);

  const addLine = (line) => {
    setCart((c) => {
      const idx = c.lines.findIndex((l) => l.sku === line.sku && l.colour === line.colour);
      let lines;
      if (idx >= 0) {
        lines = c.lines.map((l, i) => i === idx ? { ...l, qty: l.qty + (line.qty || 1) } : l);
      } else {
        lines = [...c.lines, { qty: 1, colour: "", note: "", ...line }];
      }
      return { ...c, lines };
    });
    track("add_to_cart", { sku: line.sku, colour: line.colour });
    setOpen(true);
  };

  const setQty = (sku, colour, qty) => {
    setCart((c) => ({ ...c, lines: c.lines.map((l) => l.sku === sku && l.colour === colour ? { ...l, qty: Math.max(1, qty) } : l) }));
  };
  const removeLine = (sku, colour) => setCart((c) => ({ ...c, lines: c.lines.filter((l) => !(l.sku === sku && l.colour === colour)) }));
  const updateLine = (sku, colour, patch) => setCart((c) => ({ ...c, lines: c.lines.map((l) => l.sku === sku && l.colour === colour ? { ...l, ...patch } : l) }));
  const setDiscountCode = (code) => setCart((c) => ({ ...c, discount_code: code }));
  const setAddon = (key, val) => setCart((c) => ({ ...c, [key]: val }));
  const clear = () => setCart(emptyCart);
  const count = cart.lines.reduce((a, b) => a + b.qty, 0);

  return (
    <CartCtx.Provider value={{ cart, calc, busy, open, setOpen, addLine, setQty, removeLine, updateLine, setDiscountCode, setAddon, clear, count, track }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
